import Model from "../../../../model/model"
import { ID, WebSource } from "../../../../model/types"
import { eligibleIdsFor, eligibleReadSources } from "./eligibility"
import {
  ExcludeSets,
  MOVE_KEYS,
  MoveCardState,
  MoveKey,
  NEW_PROJECT_SENTINEL,
} from "./types"

export type Rng = () => number

export const defaultRng: Rng = () => Math.random()

/** Pick one id from the pool, preferring ids not in exclude. */
export const pickFromPool = (
  ids: ID[],
  exclude: Set<ID> | undefined,
  rng: Rng = defaultRng,
): ID | null => {
  if (ids.length === 0) return null

  const excludeSet = exclude ?? new Set<ID>()
  let candidates = ids.filter((id) => !excludeSet.has(id))

  // Exhausted excludes — allow repeats so reroll never fails when pool non-empty
  if (candidates.length === 0) candidates = ids

  const index = Math.floor(rng() * candidates.length)
  return candidates[index] ?? null
}

/**
 * Read picks: balance **new** vs **queued** so a small queue is not drowned
 * by thousands of new sources. When both buckets have candidates, each
 * status is chosen with equal probability, then uniform within the bucket.
 */
export const pickReadSourceId = (
  sources: WebSource[],
  exclude: Set<ID> | undefined,
  rng: Rng = defaultRng,
): ID | null => {
  if (sources.length === 0) return null

  const excludeSet = exclude ?? new Set<ID>()
  let candidates = sources.filter((s) => !excludeSet.has(s.id))
  if (candidates.length === 0) candidates = sources

  const queued = candidates.filter((s) => s.status === "queued")
  const news = candidates.filter((s) => s.status === "new")

  let bucket: WebSource[]
  if (queued.length > 0 && news.length > 0) {
    // First rng draw chooses the status half the time
    bucket = rng() < 0.5 ? queued : news
  } else if (queued.length > 0) {
    bucket = queued
  } else {
    bucket = news
  }

  if (bucket.length === 0) return null
  const index = Math.floor(rng() * bucket.length)
  return bucket[index]?.id ?? null
}

export const pickForMove = (
  move: MoveKey,
  model: Model,
  exclude: ExcludeSets,
  rng: Rng = defaultRng,
): ID | null => {
  if (move === "read") {
    return pickReadSourceId(eligibleReadSources(model), exclude[move], rng)
  }
  return pickFromPool(eligibleIdsFor(move, model), exclude[move], rng)
}

/**
 * Build a hand: at most one card per move type, omit empty pools.
 * Order is fixed (read → extract → place → polish → wander → project).
 * Object choice is still random within each pool (scoring can replace pickForMove later).
 */
export const buildHand = (
  model: Model,
  exclude: ExcludeSets = {},
  rng: Rng = defaultRng,
): MoveCardState[] => {
  const cards: MoveCardState[] = []
  for (const move of MOVE_KEYS) {
    const objectId = pickForMove(move, model, exclude, rng)
    if (objectId != null) {
      cards.push({ move, objectId })
    } else if (move === "project") {
      // Always offer a project invitation when nothing is in progress
      cards.push({ move, objectId: NEW_PROJECT_SENTINEL })
    }
  }
  return cards
}

export const rerollMove = (
  move: MoveKey,
  model: Model,
  currentId: ID | null,
  exclude: ExcludeSets,
  rng: Rng = defaultRng,
): { objectId: ID | null; exclude: ExcludeSets } => {
  const nextExclude: ExcludeSets = { ...exclude }
  const set = new Set(exclude[move] ?? [])
  if (currentId != null) set.add(currentId)
  nextExclude[move] = set

  let objectId = pickForMove(move, model, nextExclude, rng)
  if (objectId == null && move === "project") {
    objectId = NEW_PROJECT_SENTINEL
  }
  if (objectId == null) {
    return { objectId: null, exclude: nextExclude }
  }

  // If we fell back to full pool, clear exclude for this move except current pick
  const pool = eligibleIdsFor(move, model)
  const available = pool.filter((id) => !set.has(id))
  if (
    available.length === 0 &&
    pool.length > 0 &&
    objectId !== NEW_PROJECT_SENTINEL
  ) {
    nextExclude[move] = new Set([objectId])
  }

  return { objectId, exclude: nextExclude }
}

/** Drop a card if its object is no longer eligible; otherwise keep. */
export const refreshCard = (
  card: MoveCardState,
  model: Model,
  exclude: ExcludeSets,
  rng: Rng = defaultRng,
): MoveCardState | null => {
  if (card.move === "project" && card.objectId === NEW_PROJECT_SENTINEL) {
    const objectId = pickForMove("project", model, exclude, rng)
    // Prefer a real project if one appeared; else keep invitation
    return { move: "project", objectId: objectId ?? NEW_PROJECT_SENTINEL }
  }

  const pool = eligibleIdsFor(card.move, model)
  if (pool.includes(card.objectId)) return card
  const objectId = pickForMove(card.move, model, exclude, rng)
  if (objectId == null) {
    if (card.move === "project") {
      return { move: "project", objectId: NEW_PROJECT_SENTINEL }
    }
    return null
  }
  return { move: card.move, objectId }
}

/**
 * Re-validate a saved hand against current model eligibility.
 * Keeps process order; replaces dead objects; drops empty moves.
 * Always ensures a project slot (real or "start new").
 */
export const restoreHand = (
  cards: MoveCardState[],
  model: Model,
  exclude: ExcludeSets,
  rng: Rng = defaultRng,
): MoveCardState[] => {
  const byMove = new Map(cards.map((c) => [c.move, c]))
  const next: MoveCardState[] = []
  for (const move of MOVE_KEYS) {
    const existing = byMove.get(move)
    if (existing) {
      const refreshed = refreshCard(existing, model, exclude, rng)
      if (refreshed) next.push(refreshed)
    } else {
      // Fill slots added after the hand was saved (e.g. new Polish move)
      const objectId = pickForMove(move, model, exclude, rng)
      if (objectId != null) {
        next.push({ move, objectId })
      } else if (move === "project") {
        next.push({ move, objectId: NEW_PROJECT_SENTINEL })
      }
    }
  }
  return next
}
